import React, { useState, useEffect } from "react";
import axios from "axios";
import { MessageSquare, Globe, Send, RefreshCw, User, Bot, PauseCircle, PlayCircle, ExternalLink } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Convierte URLs en enlaces clicables en la transcripción.
function renderText(text) {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s)]+)/g;
  return String(text).split(urlRegex).map((part, i) => {
    if (part.match(urlRegex)) {
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline text-orange-300 hover:text-orange-200 break-all inline-flex items-center gap-1">{part}<ExternalLink className="w-3 h-3" /></a>;
    }
    return <span key={i}>{part}</span>;
  });
}

// Panel de monitoreo de conversaciones del Asistente HEBEH (web + WhatsApp).
export default function ChatbotMonitorTab() {
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(false);
  const [channelFilter, setChannelFilter] = useState("all");
  const [humanMsg, setHumanMsg] = useState("");
  const [sending, setSending] = useState(false);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const q = channelFilter !== "all" ? `?channel=${channelFilter}` : "";
      const { data } = await axios.get(`${API}/api/admin/chatbot/sessions${q}`);
      if (data?.success) setSessions(data.sessions || []);
    } catch (e) {
      console.error("chatbot sessions error", e?.response?.status, e?.message);
    } finally {
      setLoading(false);
    }
  };

  const openSession = async (sid) => {
    setSelected(sid);
    setTranscript(null);
    try {
      const { data } = await axios.get(`${API}/api/admin/chatbot/sessions/${sid}`);
      if (data?.success) setTranscript(data.session);
    } catch (e) {
      console.error("chatbot transcript error", e?.response?.status, e?.message);
    }
  };

  const takeOver = async () => {
    if (!selected) return;
    await axios.post(`${API}/api/admin/chatbot/sessions/${selected}/take-over`);
    await openSession(selected);
  };
  const releaseBot = async () => {
    if (!selected) return;
    await axios.post(`${API}/api/admin/chatbot/sessions/${selected}/release`);
    await openSession(selected);
  };
  const sendHuman = async () => {
    const text = humanMsg.trim();
    if (!text || !selected || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/api/admin/chatbot/sessions/${selected}/send`, { message: text });
      setHumanMsg("");
      await openSession(selected);
    } catch (e) {
      console.error(e);
    } finally { setSending(false); }
  };

  useEffect(() => { loadSessions(); }, [channelFilter]);

  const fmtDate = (s) => {
    if (!s) return "";
    try {
      const d = new Date(s);
      return d.toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return s; }
  };

  return (
    <div className="space-y-4" data-testid="chatbot-monitor-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-orange-400" />
            Conversaciones del Chatbot IA
          </h2>
          <p className="text-slate-400 text-sm mt-1">Monitorea el Asistente HEBEH (web + WhatsApp) para corregir errores y detectar leads calientes.</p>
        </div>
        <div className="flex items-center gap-2">
          {["all", "web", "whatsapp"].map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setChannelFilter(c)}
              data-testid={`chatbot-filter-${c}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                channelFilter === c
                  ? "bg-orange-500 text-white"
                  : "bg-slate-800 text-slate-400 hover:text-white"
              }`}
            >
              {c === "all" ? "Todas" : c === "web" ? "Web" : "WhatsApp"}
            </button>
          ))}
          <button
            type="button"
            onClick={loadSessions}
            className="p-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-colors"
            aria-label="Refrescar"
            data-testid="chatbot-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        {/* Lista */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between">
            <span className="text-slate-300 text-sm font-medium">{sessions.length} conversaciones</span>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-slate-700/50">
            {sessions.length === 0 && !loading && (
              <div className="p-6 text-center text-slate-500 text-sm">Aún no hay conversaciones.</div>
            )}
            {sessions.map((s) => (
              <button
                key={s.session_id}
                type="button"
                onClick={() => openSession(s.session_id)}
                data-testid={`chatbot-session-${s.session_id}`}
                className={`w-full text-left px-4 py-3 hover:bg-slate-700/30 transition-colors ${
                  selected === s.session_id ? "bg-slate-700/40 border-l-2 border-orange-500" : ""
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  {s.channel === "whatsapp" ? (
                    <Send className="w-3.5 h-3.5 text-green-400" />
                  ) : (
                    <Globe className="w-3.5 h-3.5 text-blue-400" />
                  )}
                  <span className="text-white text-sm font-medium truncate">
                    {s.channel === "whatsapp" ? (s.phone || "Sin teléfono") : "Web · " + s.session_id.slice(0, 8)}
                  </span>
                  {s.handoff && (
                    <span className="ml-auto text-[10px] uppercase tracking-wider bg-orange-500/20 text-orange-400 px-1.5 py-0.5 rounded">
                      Handoff
                    </span>
                  )}
                </div>
                <p className="text-slate-400 text-xs line-clamp-2 leading-relaxed">
                  <span className="text-slate-500">{s.last_message_role === "user" ? "Cliente: " : "Bot: "}</span>
                  {s.last_message_preview || "(sin mensajes)"}
                </p>
                <p className="text-slate-600 text-[10px] mt-1">{fmtDate(s.updated_at)}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Transcripción */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden flex flex-col">
          {!transcript && (
            <div className="flex-1 flex items-center justify-center p-10 text-center text-slate-500">
              <div>
                <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="text-sm">Selecciona una conversación para ver la transcripción completa.</p>
              </div>
            </div>
          )}
          {transcript && (
            <>
              <div className="px-4 py-3 border-b border-slate-700 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium">
                    {transcript.channel === "whatsapp" ? (
                      <><Send className="inline w-3.5 h-3.5 text-green-400 mr-1.5" />WhatsApp · {transcript.phone}</>
                    ) : (
                      <><Globe className="inline w-3.5 h-3.5 text-blue-400 mr-1.5" />Web · {transcript.session_id.slice(0, 12)}...</>
                    )}
                  </p>
                  <p className="text-slate-500 text-xs mt-0.5">
                    Iniciada: {fmtDate(transcript.created_at)} · Última: {fmtDate(transcript.updated_at)}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {transcript.bot_paused ? (
                    <button onClick={releaseBot} data-testid="release-bot-btn" className="px-3 py-1.5 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 text-xs hover:bg-emerald-500/30 flex items-center gap-1.5">
                      <PlayCircle className="w-3.5 h-3.5" /> Devolver al bot
                    </button>
                  ) : (
                    <button onClick={takeOver} data-testid="take-over-btn" className="px-3 py-1.5 rounded-lg bg-orange-500/20 border border-orange-500/40 text-orange-300 text-xs hover:bg-orange-500/30 flex items-center gap-1.5">
                      <PauseCircle className="w-3.5 h-3.5" /> Tomar control
                    </button>
                  )}
                </div>
              </div>
              {transcript.handoff_summary && (
                <div className="px-4 py-2 bg-orange-500/10 border-b border-orange-500/30">
                  <p className="text-orange-400 text-xs font-medium mb-0.5">Resumen para asesor:</p>
                  <p className="text-slate-300 text-xs whitespace-pre-wrap">{transcript.handoff_summary}</p>
                </div>
              )}
              <div className="flex-1 overflow-y-auto max-h-[60vh] p-4 space-y-3">
                {(transcript.messages || []).map((m, i) => (
                  <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    {m.role !== "user" && (
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        m.role === "human_advisor" ? "bg-blue-500/20" : "bg-orange-500/20"
                      }`}>
                        {m.role === "human_advisor" ? <User className="w-3.5 h-3.5 text-blue-400" /> : <Bot className="w-3.5 h-3.5 text-orange-400" />}
                      </div>
                    )}
                    <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-blue-500/80 text-white rounded-br-sm"
                        : m.role === "human_advisor"
                        ? "bg-blue-600/60 text-white rounded-bl-sm border border-blue-400/40"
                        : "bg-slate-700/70 text-slate-100 rounded-bl-sm"
                    }`}>
                      {m.role === "human_advisor" && (
                        <div className="text-[10px] uppercase tracking-widest text-white/80 mb-1">Asesor</div>
                      )}
                      {renderText(m.content)}
                      {m.tools && m.tools.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-white/10 text-[10px] text-slate-300 space-y-0.5">
                          {m.tools.map((t, j) => (
                            <div key={j}>🔧 <span className="font-mono">{t.name}</span></div>
                          ))}
                        </div>
                      )}
                      <div className="mt-1 text-[9px] text-white/40 text-right">{fmtDate(m.at)}</div>
                    </div>
                    {m.role === "user" && (
                      <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <User className="w-3.5 h-3.5 text-blue-400" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Composer solo cuando el humano está en control */}
              {transcript.bot_paused && (
                <div className="border-t border-slate-700 p-3 bg-slate-900/50">
                  <div className="flex items-end gap-2 bg-slate-800 rounded-xl border border-slate-700 focus-within:border-orange-500/50 px-3 py-2">
                    <textarea
                      value={humanMsg}
                      onChange={(e) => setHumanMsg(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendHuman(); } }}
                      placeholder="Escribe como asesor..."
                      rows={1}
                      disabled={sending}
                      className="flex-1 resize-none bg-transparent outline-none text-white text-sm placeholder:text-slate-500 max-h-24"
                      data-testid="handoff-input"
                    />
                    <button
                      onClick={sendHuman}
                      disabled={!humanMsg.trim() || sending}
                      className="w-9 h-9 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white flex items-center justify-center"
                      data-testid="handoff-send-btn"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-slate-500 text-[10px] mt-1.5 px-1">
                    El cliente recibirá este mensaje {transcript.channel === "whatsapp" ? "por WhatsApp" : "en el chat web"}. El bot está pausado.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
